import { FC } from "react";

import Box from "@mui/material/Box";
import { formatNarrowCurrencyAmount, getDollarValue, getField } from "@simplyk/common";

import { CampaignCard } from "../../CampaignCard/CampaignCard";
import { getSupportFormCampaignCategory } from "../../CampaignCard/helpers/campaignCategory";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { useTranslate } from "@/common/hooks/useTranslate";
import { OrganizationPageOutput } from "@/common/types/trpc";
import { FormThermometer } from "@/forms-design-system/FormThermometer/FormThermometer";
import { FormTypography } from "@/forms-design-system/FormTypography";

interface SupportFormCardProps {
  form: OrganizationPageOutput["supportForms"][number];
}

export const SupportFormCard: FC<SupportFormCardProps> = ({ form }) => {
  const { t } = useTranslate();
  const { locale, isoLocale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { trackBackgroundColor, descriptionFadeColor } = useDynamicThemeAwareVariables(({ formsPalette, mode }) => ({
    trackBackgroundColor: mode === "light" ? formsPalette.surface.moderate : formsPalette.surface.intense,
    descriptionFadeColor: mode === "light" ? formsPalette.surface.quiet : formsPalette.surface.moderate,
  }));
  const category = getSupportFormCampaignCategory({ formType: form.formType, formCategory: form.formCategory });
  const field = getField(form.fields, locale);
  const target = form.target?.targetValue ? form.target : null;

  return (
    <CampaignCard
      variant="supportForm"
      title={field?.title || ""}
      url={form.url}
      category={category}
      bannerUrl={form.bannerUrl || null}
      bannerVideoUrl={form.bannerVideoUrl ?? null}
      isSoldOut={form.isSoldOut}
      isSalesClosed={form.isSalesClosed}
      onClick={() => logAmplitudeEvent(AmplitudeEvents.OrgPageCampaignOpened, { campaign_type: category })}
      slots={{
        details: target ? (
          <FormThermometer
            sx={{ backgroundColor: "transparent", padding: 0 }}
            components={{
              progress: {
                sx: (theme) => ({
                  height: theme.spacing(1.25),
                  borderRadius: theme.radius(0.5),
                  backgroundColor: trackBackgroundColor,
                }),
              },
            }}
            label={
              <FormTypography variant="caption" vibe="moderate">
                {t("organization", "thermometer", {
                  value: formatNarrowCurrencyAmount(getDollarValue(target.volume), {
                    locale: isoLocale,
                    currency: target.currency,
                    withCents: false,
                  }),
                  goal: formatNarrowCurrencyAmount(getDollarValue(target.targetValue), {
                    locale: isoLocale,
                    currency: target.currency,
                    withCents: false,
                  }),
                })}
              </FormTypography>
            }
            percentage={(target.volume / target.targetValue) * 100}
            hidePercentage
          />
        ) : field?.plainTextDescription ? (
          <Box
            sx={(theme) => ({
              position: "relative",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              ...theme.typography.body2,
              color: theme.palette.forms.text.moderate,
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: theme.spacing(2.75),
                background: `linear-gradient(180deg, transparent 0%, ${descriptionFadeColor} 100%)`,
                pointerEvents: "none",
              },
            })}
          >
            {field.plainTextDescription}
          </Box>
        ) : null,
      }}
    />
  );
};
